// frontend/src/admin/v2/NotificationTriggers.jsx
import { useEffect, useState, useMemo, useRef, useId, useCallback } from 'react'
import axios from 'axios'
import {
  Stack, Box, Typography, Alert, Skeleton, Switch, TextField,
  IconButton, Tooltip, Paper, Divider, Chip,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { toast } from 'sonner'
import { SP, FS } from '../../theme/tokens'

const API = process.env.REACT_APP_API_BASE_URL

/**
 * NotificationTriggers — /admin/notifications page.
 *
 * @description
 *   Per-trigger admin UI: toggle enable/disable, edit which channels the trigger
 *   fires on (in-app / push / email / digest), set min_seconds_between override,
 *   configure quiet hours window. Saving fires PUT .../channels, PUT .../throttle
 *   and POST .../toggle. Channel edits change the trigger's default_channels —
 *   the max set; the dispatcher can only subtract from it per user prefs.
 *
 * @useWhen
 *   - Admin wants a trigger to start (or stop) sending email without a migration
 *     (e.g. turn email on for rma.case.customer_reply_received)
 *   - Admin needs to silence a noisy trigger temporarily without disabling it
 *     entirely (e.g. set min_seconds_between=3600 for a 1h cooldown override)
 *   - Admin wants to mute push+email for a trigger overnight (e.g. quiet
 *     window 22-7 — bell + digest still fire)
 *
 * @related
 *   - UserPreferencesPanel — per-user channel prefs (different scope)
 *   - NotificationTester — fire test notifications through dispatcher
 */
const CHANNEL_OPTS = [
  ['in_app', 'In-App'],
  ['push', 'Push'],
  ['email', 'E-Mail'],
  ['digest_morning', 'Digest'],
]

// 🔴 M6-Nachbesserung 3: lesen statt raten.
//
// Leer heisst „Standard" (`null`) — das ist die einzige Ausnahme. Alles andere
// muss eine GANZE Zahl im erlaubten Bereich sein, und der Grund steht am Backend:
// `backend/notify/registry.py:154-156` schreibt die drei Werte mit `int(...)` in
// die Spalte. `int('7.5')` wirft dort ValueError (HTTP 400), `int(7.5)`
// schneidet STILL auf 7 ab — der Nutzer sieht „gespeichert" und der Wert ist ein
// anderer. Die Stundenspanne prueft `registry.py:137` (`0 <= int(h) <= 23`), die
// Drosselung `registry.py:141` (`>= 0`); die Route
// (`backend/notify/admin_routes.py:130-136`) reicht nur durch, sie prueft nicht.
// Geprueft wird deshalb hier, VOR dem Senden.
//
// Hausform der Komma-Umschrift: `components/po/SendungDialog.jsx:268-277` (die
// Zifferntastatur liefert auf deutschen Geraeten ein Komma; `Number('1,5')` ist
// `NaN`).
const alsZahl = (v) => {
  if (v === '' || v == null) return null
  const roh = String(v).replace(',', '.').trim()
  if (roh === '') return null
  const n = Number(roh)
  return Number.isFinite(n) ? n : null
}

/** Ganze Stunde 0..23; `null` = leer (Standard), `undefined` = unlesbar. */
const alsStunde = (v) => {
  if (v === '' || v == null) return null
  const n = alsZahl(v)
  return n !== null && Number.isInteger(n) && n >= 0 && n <= 23 ? n : undefined
}

/** Ganze Sekundenzahl; `null` = leer (Standard), `undefined` = unlesbar.
 *  Eine negative Zahl laesst diese Pruefung durch — sie faengt erst das Backend
 *  (`registry.py:141`, eigener englischer Text), nicht still, sondern als 400. */
const alsSekunden = (v) => {
  if (v === '' || v == null) return null
  const n = alsZahl(v)
  return n !== null && Number.isInteger(n) ? n : undefined
}

// Die Meldungen der drei Felder, an EINER Stelle. `feldFehler` traegt je Feld den
// Schluessel, nicht `true` — seit M6-Nachbesserung 4 kann dasselbe Feld zwei
// verschiedene Gruende haben (Bereich / fehlender Partner).
const FEHLERTEXT = {
  sekunden: 'Enter a whole number of seconds.',
  bereich: 'Enter a whole hour from 0 to 23.',
  paar: 'Set both quiet hours, or neither.',
}

function TriggerRow({ trigger, onUpdate, onMeldung }) {
  const [enabled, setEnabled] = useState(trigger.enabled)
  const [channels, setChannels] = useState(trigger.default_channels || [])
  const [chSaving, setChSaving] = useState(false)
  const [minSec, setMinSec] = useState(trigger.min_seconds_between ?? '')
  const [qStart, setQStart] = useState(trigger.quiet_start_hour ?? '')
  const [qEnd, setQEnd] = useState(trigger.quiet_end_hour ?? '')
  const [savingState, setSavingState] = useState('idle')  // idle | pending | saving | saved | error
  // 🔴 M6-Nachbesserung 3: Feldfehler je Feld — nur im Fehlerfall sichtbar
  // (`helperText`), geloescht, sobald im Feld getippt wird. Hausform:
  // `components/fca/FCAForm.jsx:61` (`fehlerWeg`).
  const [feldFehler, setFeldFehler] = useState({})  // { minSec?, qStart?, qEnd? }
  // 🔴 M6-Nachbesserung 4 (Regel-16-Tor W2/W3): die Spanne 0–23 der zwei
  // Stundenfelder ist seit Nachbesserung 3 aus dem Ruhezustand verschwunden (sie
  // stand nur noch im Fehlerfall). Sie kommt als **versteckter** Hinweis zurueck,
  // an den `<input>` gebunden (`aria-describedby`) — Hausform fuer visuell
  // versteckten Text: `visuallyHidden` aus `@mui/utils`, wie
  // `admin/v2/components/StatusPill.jsx:49`. Die Kennung kommt aus `useId`, damit
  // mehrere Zeilen auf derselben Seite sich nicht ueberschreiben.
  const quietHinweisId = useId()
  // 🔴 Feste Kennungen fuer die zwei Stundenfelder, damit ihr Hilfetext eine
  // vorhersagbare Kennung traegt (`${id}-helper-text`, MUI). `aria-describedby`
  // in `inputProps` ERSETZT MUIs eigene Verknuepfung zum Hilfetext — wer nur den
  // Spannen-Hinweis setzt, hoert im Fehlerfall „ungueltig" ohne Grund
  // (Regel-16-Wiedervorlage 3, B1; Hausform: rma/components/SetQuantityDialog.jsx).
  // Deshalb BEIDE Kennungen, die Meldung zuerst.
  const quietStartId = useId()
  const quietEndId = useId()
  const beschreibung = (feldId, fehler) =>
    [fehler ? `${feldId}-helper-text` : null, quietHinweisId].filter(Boolean).join(' ')

  /** Fehler eines Feldes loeschen, sobald der Nutzer darin tippt. */
  const fehlerWeg = (key) => setFeldFehler((f) => {
    if (!f[key]) return f
    const rest = { ...f }
    delete rest[key]
    return rest
  })

  async function toggleChannel(ch) {
    const next = channels.includes(ch) ? channels.filter((c) => c !== ch) : [...channels, ch]
    if (next.length === 0) {
      // keep ≥1 channel — mirrors the endpoint's non-empty rule; explain the no-op
      toast.info('At least one channel must stay active')
      return
    }
    setChSaving(true)
    try {
      const res = await axios.put(
        `${API}/api/admin/notify/triggers/${trigger.trigger_key}/channels`,
        { channels: next },
      )
      setChannels(res.data.default_channels || next)
      onUpdate(res.data)
    } catch (e) {
      toast.error(`Channel save failed for ${trigger.trigger_key}: ${e?.response?.data?.error || e.message}`)
    } finally {
      setChSaving(false)
    }
  }

  const debounceRef = useRef(null)
  const lastSavedRef = useRef({
    enabled: trigger.enabled,
    minSec: trigger.min_seconds_between ?? '',
    qStart: trigger.quiet_start_hour ?? '',
    qEnd: trigger.quiet_end_hour ?? '',
  })

  // Auto-save: debounce 600ms after the last edit, then POST/PUT.
  // Toggle changes save with shorter debounce (150ms) — feels instant.
  useEffect(() => {
    const last = lastSavedRef.current
    const dirty = (
      enabled !== last.enabled
      || String(minSec) !== String(last.minSec)
      || String(qStart) !== String(last.qStart)
      || String(qEnd) !== String(last.qEnd)
    )
    if (!dirty) return

    setSavingState('pending')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const toggleOnly = (
      enabled !== last.enabled
      && String(minSec) === String(last.minSec)
      && String(qStart) === String(last.qStart)
      && String(qEnd) === String(last.qEnd)
    )
    const delay = toggleOnly ? 150 : 600
    debounceRef.current = setTimeout(async () => {
      setSavingState('saving')
      // 🔴 M6-Nachbesserung 3: pruefen, BEVOR gesendet wird. `undefined` heisst
      // „unlesbar" (leer ist `null` = Standard) — dann wird die Drosselung NICHT
      // gesendet und der gespeicherte Wert bleibt stehen, statt still geloescht
      // zu werden (Regel 22: „unbekannt ist nie 0"; hier: unlesbar ist nie
      // „Standard").
      const ms = alsSekunden(minSec)
      const qs = alsStunde(qStart)
      const qe = alsStunde(qEnd)
      const fehler = {}
      if (ms === undefined) fehler.minSec = 'sekunden'
      if (qs === undefined) fehler.qStart = 'bereich'
      if (qe === undefined) fehler.qEnd = 'bereich'
      // 🔴 M6-Nachbesserung 4 (Regel-15-Tor W4): die zwei Ruhezeiten sind ein
      // PAAR. `registry.py:139-140` lehnt ein halb gesetztes Paar ab ("must set
      // both quiet_start_hour and quiet_end_hour together") — als 400, also als
      // Toast, nicht am Feld. Geprueft wird deshalb hier, am LEEREN Feld, VOR
      // dem Senden. Nur pruefbar, wenn BEIDE lesbar sind: ein unlesbares Feld
      // traegt schon seinen eigenen Fehler, und ein zweiter darauf waere geraten.
      if (qs !== undefined && qe !== undefined) {
        if (qs === null && qe !== null) fehler.qStart = 'paar'
        if (qe === null && qs !== null) fehler.qEnd = 'paar'
      }
      setFeldFehler(fehler)
      if (Object.keys(fehler).length > 0) {
        // Der Schalter haengt nicht an der Drosselung: hat sich NUR er geaendert,
        // laeuft sein POST wie bisher — ein unlesbares Feld blockiert ihn nicht.
        if (enabled !== last.enabled) {
          try {
            await axios.post(`${API}/api/admin/notify/triggers/${trigger.trigger_key}/toggle`, { enabled })
            lastSavedRef.current = { ...lastSavedRef.current, enabled }
          } catch (e) {
            setSavingState('error')
            toast.error(`Save failed for ${trigger.trigger_key}: ${e?.response?.data?.error || e.message}`)
            return
          }
        }
        setSavingState('idle')  // NICHT „saved" — es wurde nichts gespeichert
        return
      }
      try {
        if (enabled !== last.enabled) {
          await axios.post(`${API}/api/admin/notify/triggers/${trigger.trigger_key}/toggle`, { enabled })
        }
        const body = {
          min_seconds_between: ms,
          quiet_start_hour: qs,
          quiet_end_hour: qe,
        }
        const res = await axios.put(`${API}/api/admin/notify/triggers/${trigger.trigger_key}/throttle`, body)
        lastSavedRef.current = { enabled, minSec, qStart, qEnd }
        onUpdate(res.data)
        setSavingState('saved')
        // Decay back to idle after 1.5s
        setTimeout(() => setSavingState((s) => (s === 'saved' ? 'idle' : s)), 1500)
      } catch (e) {
        setSavingState('error')
        toast.error(`Save failed for ${trigger.trigger_key}: ${e?.response?.data?.error || e.message}`)
      }
    }, delay)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [enabled, minSec, qStart, qEnd, trigger.trigger_key, onUpdate])

  async function clearOverrides() {
    setSavingState('saving')
    try {
      const res = await axios.put(`${API}/api/admin/notify/triggers/${trigger.trigger_key}/throttle`, { clear: true })
      onUpdate(res.data)
      setMinSec('')
      setQStart('')
      setQEnd('')
      // 🔴 M6-Nachbesserung 4 (Regel-16-Tor W1): der Feldfehler gehoert dem
      // Feld, nicht dem Wert. Ohne diese Zeile ueberlebt die rote Meldung ihren
      // Gegenstand: der Entpreller laeuft nach dem Zuruecksetzen zwar wieder an,
      // bricht aber bei `!dirty` ab (`lastSavedRef` ist hier mitgezogen), und der
      // Zweig, der `feldFehler` leert, wird nie erreicht. Ein leeres Feld, das
      // „geben Sie eine ganze Zahl ein" behauptet, ist genau die Klasse, gegen
      // die dieser Zweig sonst antritt.
      setFeldFehler({})
      lastSavedRef.current = { enabled, minSec: '', qStart: '', qEnd: '' }
      setSavingState('saved')
      setTimeout(() => setSavingState((s) => (s === 'saved' ? 'idle' : s)), 1500)
    } catch (e) {
      setSavingState('error')
      toast.error(`Clear failed: ${e?.response?.data?.error || e.message}`)
    }
  }

  const statusLabel = {
    idle: '',
    pending: 'Editing…',
    saving: 'Saving…',
    saved: 'Saved ✓',
    error: 'Save failed',
  }[savingState]
  const statusColor = {
    pending: 'text.disabled',
    saving: 'text.secondary',
    saved: 'success.main',
    error: 'error.main',
  }[savingState] || 'text.disabled'

  // 🔴 UI2 Teil 8, K-9 (`paar-fehler-ohne-aria-live`): die Meldungen dieser Zeile
  // stehen am `helperText` und sind ueber `aria-describedby` an ihr Feld gebunden.
  // Eine AENDERUNG von `aria-describedby` liest ein Vorlesegeraet NICHT vor,
  // solange der Fokus im Feld steht — genau der Moment, in dem sie gebraucht wird:
  // beim halb gesetzten Ruhezeit-PAAR steht der Fokus im GESETZTEN Feld, waehrend
  // die Meldung am LEEREN haengt. Gleiche Texte (beide Stundenfelder koennen
  // „bereich" tragen) fallen zusammen.
  //
  // 🔴 Angesagt wird sie von der SEITE, nicht von der Zeile (Hausentscheidung O21,
  // `PODetail.jsx:236-244`): in einer ZEILENLISTE traegt EINE Sammelregion am Kopf
  // die zuletzt entstandene Meldung. Eine Region je Zeile waeren hier bis zu 2N auf
  // einem Bildschirm — die Seite rendert ALLE Trigger ungeklappt, nach Domaene
  // gruppiert — und sie forkte O21, ohne den Fork zu benennen. Die Zeile meldet
  // ihren Text deshalb nach oben (`onMeldung`) und behaelt die sichtbare Meldung
  // am Feld; die Region selbst haengt in `NotificationTriggers`.
  //
  // Der Text steht damit ZWEIMAL im Baum: sichtbar am Feld (`helperText`, rot) und
  // einmal in der Sammelregion der Seite (dort mit dem Namen des Triggers davor).
  // Das ist Absicht — der `helperText` ist die sichtbare Meldung, die Region die
  // hoerbare; ein Textknoten kann nicht beide Rollen tragen, ohne die Zeile zu
  // strecken (der immer sichtbare `helperText` schob die Felder um 11 px, s.
  // `:379-385`). Wer die sichtbare Meldung sucht, grenzt sie mit
  // `.MuiFormHelperText-root` ab (`NotificationTriggers.test.jsx`).
  const feldMeldung = [...new Set(
    [feldFehler.minSec, feldFehler.qStart, feldFehler.qEnd]
      .filter(Boolean)
      .map((k) => FEHLERTEXT[k]),
  )].join(' ')
  // Der NAME des Triggers steht davor: die Sammelregion gehoert der Seite, ohne
  // ihn waere nicht zu hoeren, WELCHE Zeile sich meldet.
  const ansage = feldMeldung ? `${trigger.label}: ${feldMeldung}` : ''
  useEffect(() => { onMeldung(trigger.trigger_key, ansage) }, [ansage, trigger.trigger_key, onMeldung])

  return (
    <Paper variant="outlined" sx={{ p: SP.card }}>
      {/* Nur fuer Sprachausgaben — `visuallyHidden` nimmt das Element aus dem
          Fluss (`position: absolute`), also aendert es die Zeile nicht. */}
      <Box component="span" id={quietHinweisId} sx={visuallyHidden}>
        Whole hour from 0 to 23.
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={SP.card}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: FS.sm, fontWeight: 600 }}>
              {trigger.trigger_key}
            </Typography>
            <Chip label={trigger.default_urgency} size="small" />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {trigger.label} — {trigger.description}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {CHANNEL_OPTS.map(([key, chLabel]) => {
              const active = channels.includes(key)
              return (
                <Chip
                  key={key}
                  label={chLabel}
                  size="small"
                  color={active ? 'primary' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={() => toggleChannel(key)}
                  disabled={chSaving}
                  aria-label={`Toggle ${chLabel} channel (currently ${active ? 'on' : 'off'})`}
                  aria-pressed={active}
                />
              )
            })}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={enabled ? 'Trigger enabled' : 'Trigger disabled (no notifications fire)'}>
            <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} size="small" />
          </Tooltip>
          {/* 🔴 M6: `inputMode="numeric"` statt `type="number"` an allen drei
              Feldern — sonst erscheint auf dem Telefon die BUCHSTABENtastatur.
              Ganze Sekunden/Stunden → `numeric`. Hausform:
              components/po/SendungDialog.jsx:637-639.
              🔴 M6-Nachbesserung (Regel-16-Tor W5): `min`/`max` mitgefallen — im
              Textfeld wirkungslos. Die Stundenspanne ist eine fachliche Aussage;
              sie wird jetzt GEPRUEFT (`alsStunde`, vor dem Senden), statt nur
              behauptet zu werden. Das Backend prueft sie ebenfalls
              (backend/notify/registry.py:137), aber erst nach dem Senden.
              🔴 M6-Nachbesserung 2 (Regel-16-Tor W8): die Spanne stand zuerst im
              ETIKETT („Quiet start (0–23 h)") — 19 Zeichen in einem 110 px
              breiten Feld; ein gekuerztes Etikett verliert genau sie. Danach im
              `helperText` (wie VendorDetails.jsx:1056).
              🔴 M6-Nachbesserung 3 (2026-10-09, im Browser gemessen): ein IMMER
              sichtbarer `helperText` streckt den Kasten auf 59 px, und weil die
              Zeile `alignItems="center"` traegt, rutschen „Quiet start" und
              „Quiet end" gegenueber „Min sec between" um 11 px nach oben. Der
              Hilfetext war also die falsche Stelle. Danach: die EINHEIT im Feld
              (`InputAdornment` „h") und die PRUEFUNG beim Speichern; der
              `helperText` trug nur noch den Fehlerfall.
              🔴 M6-Nachbesserung 4 (Regel-16-Tor W2/W3, Regel-15-Tor W1): das
              `InputAdornment` fiel wieder weg — es ist eine Zierde NEBEN dem
              `<input>`, also nicht Teil des zugaenglichen Namens, und die zwei
              Felder waren damit informationsaermer als VOR M6 (dort stand
              „Quiet start (h)" im Etikett). Jetzt traegt das ETIKETT die Einheit
              („Quiet start (h)", 15 Zeichen — im Browser gemessen 88 px Inhalt,
              im 110-px-Feld 86 px Platz; das passte nicht, deshalb ist es 116 px breit), der PLACEHOLDER die Spanne (`0–23`, im Ruhezustand sichtbar)
              und ein versteckter Hinweis dieselbe Spanne fuer Sprachausgaben
              (`aria-describedby`, s. `quietHinweisId` oben). Die Spanne wird
              ausserdem GEPRUEFT — und das Paar: ein halb gesetztes Paar haelt an
              (`registry.py:139-140`). */}
          <TextField
            label="Min sec between"
            size="small"
            value={minSec}
            onChange={(e) => { setMinSec(e.target.value); fehlerWeg('minSec') }}
            placeholder="default"
            sx={{ width: 136 }}
            inputProps={{ inputMode: 'numeric' }}
            error={Boolean(feldFehler.minSec)}
            helperText={feldFehler.minSec ? FEHLERTEXT[feldFehler.minSec] : undefined}
          />
          <TextField
            label="Quiet start (h)"
            size="small"
            value={qStart}
            onChange={(e) => { setQStart(e.target.value); fehlerWeg('qStart') }}
            placeholder="0–23"
            sx={{ width: 116 }}
            id={quietStartId}
            inputProps={{ inputMode: 'numeric', 'aria-describedby': beschreibung(quietStartId, feldFehler.qStart) }}
            error={Boolean(feldFehler.qStart)}
            helperText={feldFehler.qStart ? FEHLERTEXT[feldFehler.qStart] : undefined}
          />
          <TextField
            label="Quiet end (h)"
            size="small"
            value={qEnd}
            onChange={(e) => { setQEnd(e.target.value); fehlerWeg('qEnd') }}
            placeholder="0–23"
            sx={{ width: 116 }}
            id={quietEndId}
            inputProps={{ inputMode: 'numeric', 'aria-describedby': beschreibung(quietEndId, feldFehler.qEnd) }}
            error={Boolean(feldFehler.qEnd)}
            helperText={feldFehler.qEnd ? FEHLERTEXT[feldFehler.qEnd] : undefined}
          />
          <Box sx={{ minWidth: 80, textAlign: 'right' }}>
            {statusLabel && (
              <Typography
                variant="caption"
                sx={{ fontSize: FS.xs, color: statusColor, fontWeight: savingState === 'saved' ? 600 : 400 }}
              >
                {statusLabel}
              </Typography>
            )}
          </Box>
          <Tooltip title="Clear overrides (use defaults)">
            <span>
              <IconButton size="small" onClick={clearOverrides} disabled={savingState === 'saving'}
                aria-label="Clear overrides (use defaults)">
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default function NotificationTriggers() {
  const [triggers, setTriggers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // 🔴 UI2 Teil 8, K-9 (Hausentscheidung O21, `PODetail.jsx:236-244`): die EINE
  // Live-Region der Zeilenliste. Sie haelt die ZULETZT entstandene Zeilenmeldung samt
  // Trigger-Namen — nicht jede Zeile ihre eigene (das waeren bis zu 2N).
  // `melde` ist stabil: `useCallback` ohne Abhaengigkeit plus funktionales
  // `setState`, sonst liefe der Melde-Effekt der Zeilen bei jedem Render.
  // `text || s.key === key` laesst eine Zeile nur ihre EIGENE Meldung loeschen:
  // raeumt eine ANDERE Zeile ihren Fehler weg, bleibt die zuletzt angesagte
  // stehen — sonst leere ein fremdes Aufraeumen die Region still.
  const [sammelAnsage, setSammelAnsage] = useState({ key: '', text: '' })
  const melde = useCallback((key, text) => {
    setSammelAnsage((s) => (text || s.key === key ? { key, text } : s))
  }, [])

  useEffect(() => {
    axios.get(`${API}/api/admin/notify/triggers`)
      .then(r => setTriggers(r.data.triggers || []))
      .catch(e => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [])

  function updateOne(updated) {
    setTriggers(prev => prev.map(t => t.trigger_key === updated.trigger_key ? updated : t))
  }

  const byDomain = useMemo(() => {
    const groups = {}
    for (const t of triggers) {
      (groups[t.domain] = groups[t.domain] || []).push(t)
    }
    return groups
  }, [triggers])

  if (loading) {
    return <Stack spacing={1}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={80} />)}</Stack>
  }

  return (
    <Stack spacing={SP.section}>
      {error && <Alert severity="error" variant="outlined">{error}</Alert>}
      <Box>
        <Typography variant="h6" sx={{ fontSize: FS.lg, fontWeight: 600 }}>
          Notification triggers
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Click a channel chip to turn it on/off for a trigger (adds/removes email, push, etc. — no
          migration needed). Throttle + quiet-hours changes auto-save (~600ms debounce; toggles save
          instantly). Override the auto-backoff with a fixed minimum-seconds-between, or set a quiet
          window (UTC hours, exclusive end) when push + email are skipped. Bell + digest still fire
          during quiet hours.
        </Typography>
      </Box>

      {/* O21 — die EINZIGE Live-Region der Zeilenliste. Immer eingehaengt, wechselt nur
          den Inhalt: eine `role="status"`-Region, die gleichzeitig mit ihrem
          Inhalt in den Baum kommt, wird von Screenreadern oft NICHT vorgelesen.
          `role="status"` ist die Rolle, die `aria-live="polite"` meint (und
          `aria-atomic` still auf `true` setzt: die Ansage ist ein Satz, ganz
          vorlesen ist hier richtig) — und sie macht die Region pruefbar, ohne den
          Text ein zweites Mal zu suchen. `visuallyHidden` haelt sie aus dem Fluss. */}
      <Typography role="status" aria-live="polite" sx={visuallyHidden}>
        {sammelAnsage.text}
      </Typography>

      {Object.entries(byDomain).sort(([a], [b]) => a.localeCompare(b)).map(([domain, items]) => (
        <Box key={domain}>
          <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', mb: 1 }}>
            {domain}
          </Typography>
          <Stack spacing={1}>
            {items.map(t => (
              <TriggerRow key={t.trigger_key} trigger={t} onUpdate={updateOne} onMeldung={melde} />
            ))}
          </Stack>
          <Divider sx={{ mt: SP.card }} />
        </Box>
      ))}
    </Stack>
  )
}
